/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  IconMenu2,
  IconX,
  IconBell,
  IconSearch,
  IconUser,
  IconLogout,
  IconChevronRight,
  IconPencil,
  IconBasketCog,
  IconPackage,
  IconHeadset,
  IconPackages,
  IconFileInvoice,
  IconMapSearch,
  IconLanguage,
  IconSettings,
  IconLayoutDashboard,
  IconQrcode,
  IconWebhook,
} from "@tabler/icons-react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetch3 } from "../commonApis/commonApis";
import { useLanguage, T } from "../context/LanguageContext";
import {
  useProfileData,
  useUpdateProfilePicMutation,
  useClientPermissions,
} from "../apis/profileApis";
import toast from "react-hot-toast";
import {
  useFetchNotificationsData,
  markNotificationAsSeen,
} from "../apis/notificationApis";
import { useMerchantAllowedShippingChannels } from "../apis/integrationApis";
import { useSelector } from "react-redux";
import { selectClientPermissions } from "../features/auth/authSlice";
import { useQueryClient } from "@tanstack/react-query";
import { BulkResponseHandler } from "./BulkResponseHandler";
import { NotificationItem } from "./NotificaionItem";
import useDebounce from "../utilities/useDebounce";

interface SearchResultItem {
  entityType: "order" | "user" | "client";
  entityId: string;
  displayTitle: string;
  subText?: string;
  awbNumber?: string;
  orderName?: string;
  referenceNumber?: string;
  status?: string;
  createdAt?: string;
}

type Props = {
  notification?: number;
};

export default function Header({ notification }: Props) {
  const queryClient = useQueryClient();
  const profile = useSelector((state: any) => state.auth?.profile);
  const navigate = useNavigate();
  const location = useLocation();

  const [activeItem, setActiveItem] = useState(location.pathname);
  const [showSearch, setShowSearch] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [openResponse, setOpenResponse] = useState(false);
  const [bulkJobId, setBulkJobId] = useState<string>("");
  const [selectedNotification, setSelectedNotification] = useState<any>(null);

  // Fetch client permissions automatically if not already fetched
  const { isLoading: isLoadingPermissions } = useClientPermissions();
  const clientPermissions = useSelector(selectClientPermissions);

  const { data: UserData, refetch } = useProfileData();
  const clientId = UserData?.id || UserData?.clientId || "";
  const { data: allowedChannels } = useMerchantAllowedShippingChannels(clientId);
  const hasIntegrationAccess =
    Array.isArray(allowedChannels) && allowedChannels.length > 0;

  const navItems = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: <IconLayoutDashboard />,
      modules: ["dashboard"],
    },
    {
      name: "Order",
      path: "/orders",
      icon: <IconPackage />,
      modules: ["orders"],
    },
    {
      name: "Manifest",
      path: "/manifest",
      icon: <IconMapSearch />,
      modules: [
        "pickup_manifests",
        "linehaul_manifests",
        "return_to_shipper",
        "manifest",
      ],
    },
    {
      name: "Inventory",
      path: "/inventory-mangement",
      icon: <IconPackages />,
      modules: [
        "shelves",
        "shipment_inventory",
        "products",
        "product_inventory",
        "rtf",
        "inventory",
      ],
    },
    {
      name: "Invoice",
      path: "/invoice-management",
      icon: <IconFileInvoice />,
      modules: ["invoices", "linehaul_invoices"],
    },
    {
      name: "Integration",
      path: "/integration",
      icon: <IconBasketCog />,
      modules: ["integration"],
    },
    // {
    //   name: "Barcode/QR",
    //   path: "/barcode-qr-generator",
    //   icon: <IconQrcode />,
    //   modules: ["barcode", "barcode_qr_generator"],
    // },
    {
      name: "Webhooks",
      path: "/webhooks",
      icon: <IconWebhook />,
      modules: ["webhook", "webhooks"],
    },
    {
      name: "Help & Support",
      path: "/help-and-support",
      icon: <IconHeadset />,
      modules: ["support", "help_and_support", "tickets"],
    },
  ];

  //   const visibleNavItems = useMemo(() => {
  //   if (!clientPermissions || clientPermissions.length === 0) return navItems;
  //   return navItems.filter((item) => {
  //     if (!item.modules || item.modules.length === 0) return true;
  //     return item.modules.some((mod) =>
  //       clientPermissions.some(
  //         (p: any) =>
  //           p.module?.trim().toLowerCase() === mod.trim().toLowerCase() &&
  //           (p.operations?.includes("read") || p.operations?.includes("view")),
  //       ),
  //     );
  //   });
  // }, [clientPermissions, hasIntegrationAccess]);

  // All header tabs should always be visible. Section access is controlled on page level.
  const visibleNavItems = navItems;

  const notificationRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchFor, setSearchFor] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const debouncedSearch = useDebounce(searchFor, 300);
  const url = process.env.REACT_APP_BASE_URL;

  const { language, setLanguage, t } = useLanguage();

  const LogoComponent =
    process.env.REACT_APP_EXTERNAL_ADMIN === "true"
      ? "/logo/logo-new-najiz-exp.png"
      : "/img/dashboard-icons/logo-tracktech.svg";

  useEffect(() => setActiveItem(location.pathname), [location.pathname]);

  const handleNavClick = useCallback(
    (path: string, e?: React.MouseEvent) => {
      if (e && (e.ctrlKey || e.metaKey)) {
        window.open(path, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(path);
      setActiveItem(path);
      setShowSidebar(false);
    },
    [navigate],
  );

  const searchGlobalApi = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      setShowSearchResults(true);

      try {
        const res: any = await fetch3(
          `${url}/api/search/global?query=${encodeURIComponent(query)}`,
          "get",
        );

        if (res?.data && Array.isArray(res.data)) {
          const mapped = res.data.map((i: any) => ({
            ...i,
            subText: i.orderName
              ? `Order: ${i.orderName} | Status: ${i.status}`
              : i.subText,
          }));
          setSearchResults(mapped);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error(err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [url],
  );

  useEffect(() => {
    searchGlobalApi(debouncedSearch);
  }, [debouncedSearch, searchGlobalApi]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchFor.trim().length >= 2) {
      setShowSearchResults(false);
      setShowSearch(false);
      navigate("/orders", {
        state: { searchResults, searchQuery: searchFor },
      });
      setSearchFor("");
      setSearchResults([]);
    }
  };

  const { mutate } = useUpdateProfilePicMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    mutate(
      { companyLogo: file },
      {
        onSuccess: () => {
          toast.success("Profile picture updated!");
          refetch();
        },
        onError: (err: any) => {
          toast.error(err?.message ?? "Upload failed.");
        },
      },
    );
  };

  const handleLogout = async () => {
    try {
      await fetch3(`${url}/api/auth/logout?type=lmClient`, "get");
    } catch (e) {
      console.error(e);
    }
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    queryClient.removeQueries({ queryKey: ["getMe"] });
    queryClient.clear();
    refetch();
    navigate("/", { replace: true });
  };

  const { data: notificationsData, refetch: fetchNotifications } =
    useFetchNotificationsData(1, 20, "");

  const totalUnreadCount = useMemo(() => {
    let count = 0;
    if (typeof notification === "number" && notification > 0) {
      count = Math.max(count, notification);
    }
    if (
      notificationsData?.notifications &&
      Array.isArray(notificationsData.notifications)
    ) {
      const unreadCount = notificationsData.notifications.filter(
        (n: any) => !n.seen && !n.adminSeen && !n.isRead && !n.read,
      ).length;
      count = Math.max(count, unreadCount);
    }
    if (
      typeof notificationsData?.unseenCount === "number" &&
      notificationsData.unseenCount > 0
    ) {
      count = Math.max(count, notificationsData.unseenCount);
    }
    if (
      typeof notificationsData?.unreadCount === "number" &&
      notificationsData.unreadCount > 0
    ) {
      count = Math.max(count, notificationsData.unreadCount);
    }
    return count;
  }, [notification, notificationsData]);

  useEffect(() => {
    if (notification !== undefined) {
      fetchNotifications();
    }
  }, [notification, fetchNotifications]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotifications();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const closeNoti = (e: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(e.target as Node)
      ) {
        setShowNotification(false);
      }
    };
    document.addEventListener("mousedown", closeNoti);
    return () => document.removeEventListener("mousedown", closeNoti);
  }, []);

  useEffect(() => {
    const closeProf = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener("mousedown", closeProf);
    return () => document.removeEventListener("mousedown", closeProf);
  }, []);

  const [isIpadPro, setIsIpadPro] = useState(false);

  useEffect(() => {
    const ipadProQuery = window.matchMedia(
      "(min-device-width: 1024px) and (max-device-width: 1024px) and (min-device-height: 1366px) and (max-device-height: 1366px)",
    );

    setIsIpadPro(ipadProQuery.matches);

    const listener = (e: any) => setIsIpadPro(e.matches);
    ipadProQuery.addEventListener("change", listener);

    return () => ipadProQuery.removeEventListener("change", listener);
  }, []);

  const handleNotificationClick = useCallback(
    async (n: any) => {
      if (!n) return;

      // Open BulkResponseHandler modal if notification has a bulk job reference
      if (
        n.referenceId &&
        (n.title?.includes("Bulk") ||
          n.message?.toLowerCase().includes("bulk") ||
          n.message?.toLowerCase().includes("order"))
      ) {
        setBulkJobId(n.referenceId);
        setSelectedNotification(n);
        setOpenResponse(true);
        setShowNotification(false);
      }

      const isSeen = n.seen ?? n.adminSeen;
      if (isSeen) return;

      // Optimistic cache update
      queryClient.setQueriesData(
        {
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            String(q.queryKey[0]).toLowerCase().includes("notification"),
        },
        (old: any) => {
          if (!old?.notifications) return old;
          return {
            ...old,
            notifications: old.notifications.map((item: any) =>
              item.id === n.id ? { ...item, seen: true, adminSeen: true } : item,
            ),
            unseenCount: Math.max(0, (old.unseenCount ?? 0) - 1),
            unreadCount: Math.max(0, (old.unreadCount ?? 0) - 1),
          };
        },
      );

      try {
        await markNotificationAsSeen(n.id);
      } catch (err) {
        console.error("Failed to mark notification as seen:", err);
      } finally {
        fetchNotifications();
      }
    },
    [queryClient, fetchNotifications],
  );

  const displayAvatar =
    UserData?.companyLogo || UserData?.logo || "/img/dumy-profile.png";

  return (
    <>
      {isIpadPro ? (
        <div className="p-4 sticky top-0 z-[40]">
          <header className="flex items-center justify-between px-6 py-2 bg-[#F7F7F5]/50 backdrop-blur-md rounded-lg shadow border border-white">
            <div
              onClick={() => navigate("/dashboard")}
              className="flex items-end gap-2 cursor-pointer"
            >
              <img src={LogoComponent} alt="Logo" className="max-w-[50px]" />
              <div
                title={t("Client")}
                className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-black uppercase border border-[#212121] rounded-lg whitespace-nowrap"
              >
                {t("Client")}
              </div>
            </div>

            {/* Desktop Nav */}
            <nav
              className={`flex items-center gap-2 transition-all duration-300 bg-[#F7F7F5] rounded-lg ${showSearch ? "w-0 overflow-hidden" : "w-auto p-1"
                }`}
            >
              {visibleNavItems.map((item) => {
                const isActive =
                  activeItem === item.path ||
                  (item.path !== "/" &&
                    location.pathname.startsWith(item.path));
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(item.path, e);
                    }}
                    className={`relative group overflow-hidden px-3.5 py-2 text-xs rounded-lg whitespace-nowrap transition-all duration-300 ease-out select-none transform active:scale-[0.96] ${isActive
                      ? "bg-[#212121] text-white font-bold shadow-sm"
                      : "bg-transparent text-[#212121]/70 font-semibold hover:text-[#212121]"
                      }`}
                  >
                    {!isActive && (
                      <i className="absolute left-0 top-[120%] group-hover:-top-[40%] w-full h-[180%] bg-[#000000] rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out pointer-events-none overflow-hidden z-10">
                        <span className="absolute top-1/2 left-1/2 w-[200%] h-[200%] bg-[radial-gradient(circle,_rgba(255,255,255,0.25)_0%,_rgba(255,255,255,0.05)_45%,_transparent_70%)] gooey-bg pointer-events-none" />
                      </i>
                    )}
                    <span
                      className={`relative z-20 transition-colors duration-500 inline-block ${!isActive ? "group-hover:text-white" : ""
                        }`}
                    >
                      <T>{item.name}</T>
                    </span>
                  </a>
                );
              })}
            </nav>

            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <div className="relative" ref={searchContainerRef}>
                <div
                  className={`flex items-center transition-all duration-300 ease-in-out overflow-hidden rounded-lg cursor-pointer h-10 ${showSearch
                    ? "w-96 bg-white border-2 border-[#FBD037] shadow-[0_6px_20px_rgba(251,208,55,0.15)]"
                    : "w-10 bg-[#F7F7F5] border border-[#FFFFFF]/20 hover:border-[#FBD037] shadow-sm"
                    }`}
                >
                  <div
                    className="flex items-center justify-center w-10 h-10 text-[#212121] bg-transparent flex-shrink-0"
                    onClick={() => !showSearch && setShowSearch(true)}
                  >
                    <IconSearch strokeWidth={1.5} size={18} />
                  </div>

                  {showSearch && (
                    <>
                      <input
                        type="text"
                        placeholder={t("search awb, order...")}
                        autoFocus
                        value={searchFor}
                        onChange={(e) => setSearchFor(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        className="flex-1 h-full px-2 text-sm text-[#212121] placeholder-[#212121]/70 bg-transparent border-none outline-none"
                      />
                      <div
                        className="flex items-center justify-center w-12 h-12 text-[#212121] flex-shrink-0"
                        onClick={() => {
                          setShowSearch(false);
                          setSearchFor("");
                          setSearchResults([]);
                        }}
                      >
                        <IconX className="w-5 h-5" />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div ref={notificationRef}>
                <div
                  className="relative flex items-center justify-center w-10 h-10 border border-[#FFFFFF]/20 rounded-lg cursor-pointer bg-[#F7F7F5]"
                  onClick={() => {
                    fetchNotifications();
                    setShowNotification((p) => !p);
                  }}
                >
                  <IconBell
                    strokeWidth={1.5}
                    size={18}
                    className="text-[#212121]"
                  />
                  {totalUnreadCount > 0 ? (
                    <span className="absolute -top-1 -end-1 flex items-center justify-center min-w-[20px] h-[20px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-lg shadow-md">
                      {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
                    </span>
                  ) : null}
                </div>

                {showNotification && (
                  <div
                    className={`absolute ${language === "ar" ? "left-0" : "right-0"} z-[1000] w-96 p-4 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg`}
                  >
                    <h3 className="mb-2 text-lg">{t("Notifications")}</h3>
                    <div className="max-h-96 overflow-y-auto">
                      {notificationsData?.notifications?.length ? (
                        notificationsData.notifications
                          .map((n: any) => (
                            <NotificationItem
                              key={n.id}
                              n={n}
                              onClick={() => handleNotificationClick(n)}
                            />
                          ))
                      ) : (
                        <p className="py-6 text-sm text-center text-gray-500">
                          {t("No notifications")}
                        </p>
                      )}
                    </div>

                  </div>
                )}
              </div>

              {/* Language Switcher */}
              <div className="relative">
                <div
                  className="flex items-center justify-center w-10 h-10 border border-[#FFFFFF]/20 rounded-lg cursor-pointer bg-[#F7F7F5] transition-all hover:scale-105 duration-200"
                  onClick={() => setLanguage(language === "en" ? "ar" : "en")}
                >
                  <IconLanguage
                    strokeWidth={1.5}
                    size={18}
                    className="text-[#212121]"
                  />
                </div>
              </div>

              {/* Profile */}
              <div className="relative" ref={profileRef}>
                <div
                  className="flex items-center justify-center w-10 h-10 overflow-hidden rounded-lg cursor-pointer"
                  onClick={() => setShowProfileDropdown((p) => !p)}
                >
                  <img
                    className="object-cover w-10 h-10 rounded-lg"
                    src={displayAvatar}
                    alt="Avatar"
                  />
                </div>

                {showProfileDropdown && (
                  <div
                    className={`absolute ${language === "ar" ? "left-0" : "right-0"} z-[1000] w-72 p-0 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg`}
                  >
                    <div className="flex items-center gap-2 px-4 py-4 border-b border-[#E5E7EB]">
                      <div className="relative group">
                        <img
                          className="w-14 h-14 object-cover rounded-lg"
                          src={displayAvatar}
                          alt="Avatar"
                        />
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg opacity-0 cursor-pointer group-hover:opacity-100 transition"
                        >
                          <IconPencil size={18} color="white" />
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          className="hidden"
                          onChange={handleFileChange}
                        />
                      </div>
                      <div>
                        <h2 className="text-sm font-medium text-[#212121]">
                          {UserData?.name ??
                            UserData?.storeName ??
                            UserData?.username}
                        </h2>
                        <p className="text-xs text-[#212121]/70">
                          {UserData?.email}
                        </p>
                      </div>
                    </div>

                    <ul className="py-2 space-y-2 text-sm text-[#212121]">
                      {[
                        {
                          icon: IconUser,
                          label: "Company Info",
                          path: "/profile",
                        },
                        {
                          icon: IconSettings,
                          label: "Settings",
                          path: "/settings",
                        },
                        {
                          icon: IconHeadset,
                          label: "Help & Support",
                          path: "/help-and-support",
                        },
                      ].map((i) => (
                        <li
                          key={i.path}
                          className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                          onClick={() => {
                            navigate(i.path);
                            setShowProfileDropdown(false);
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <i.icon stroke={1.5} />
                            <T>{i.label}</T>
                          </div>
                          <IconChevronRight
                            size={22}
                            stroke={1.5}
                            color="#6B7280"
                          />
                        </li>
                      ))}

                      <li
                        className="flex items-center gap-1 px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                        onClick={() => {
                          handleLogout();
                          setShowProfileDropdown(false);
                        }}
                      >
                        <IconLogout size={22} stroke={1.5} /> <T>Logout</T>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </header>
        </div>
      ) : (
        /* Standard Layout */
        <div className="sticky top-0 z-[40]">
          <div className="p-2 sm:p-4 flex items-center justify-between">
            <div
              onClick={() => navigate("/dashboard")}
              className="flex items-end gap-1.5 sm:gap-2 cursor-pointer"
            >
              <img
                src={LogoComponent}
                alt="Logo"
                className="max-w-[100px] xs:max-w-[120px] sm:max-w-[160px]"
              />
              <div
                title={t("Client")}
                className="flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 sm:px-4 sm:py-1 text-[10px] sm:text-xs font-medium text-black uppercase border border-[#212121] rounded-lg whitespace-nowrap"
              >
                {t("Client")}
              </div>
            </div>

            <header className="px-3 sm:px-3 py-1.5 sm:py-2 bg-[#F7F7F5]/50 backdrop-blur-md rounded-lg shadow border border-white">
              {/* Desktop Nav */}
              <div className="flex items-center gap-4">
                <div className="flex items-center backdrop-blur-sm h-6 sm:h-8 rounded-lg">
                  <nav
                    className={`hidden xl:flex items-center gap-2 transition-all duration-300 ${showSearch ? "w-0 overflow-hidden" : "w-auto p-1"
                      }`}
                  >
                    {visibleNavItems.map((item) => {
                      const isActive =
                        activeItem === item.path ||
                        (item.path !== "/" &&
                          location.pathname.startsWith(item.path));
                      return (
                        <a
                          key={item.path}
                          href={item.path}
                          onClick={(e) => {
                            e.preventDefault();
                            handleNavClick(item.path, e);
                          }}
                          className={`relative group overflow-hidden px-4 py-2 text-sm rounded-lg whitespace-nowrap transition-all duration-300 ease-out select-none transform active:scale-[0.96] ${isActive
                            ? "bg-[#212121] text-white font-bold shadow-sm"
                            : "bg-transparent text-[#212121]/70 font-semibold hover:text-[#212121]"
                            }`}
                        >
                          {!isActive && (
                            <i className="absolute left-0 top-[120%] group-hover:-top-[40%] w-full h-[180%] bg-[#000000] rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out pointer-events-none overflow-hidden z-10">
                              <span className="absolute top-1/2 left-1/2 w-[200%] h-[200%] bg-[radial-gradient(circle,_rgba(255,255,255,0.25)_0%,_rgba(255,255,255,0.05)_45%,_transparent_70%)] gooey-bg pointer-events-none" />
                            </i>
                          )}
                          <span
                            className={`relative z-20 transition-colors duration-500 inline-block ${!isActive ? "group-hover:text-white" : ""
                              }`}
                          >
                            <T>{item.name}</T>
                          </span>
                        </a>
                      );
                    })}
                  </nav>

                  {/* Search */}
                  <div className="relative" ref={searchContainerRef}>
                    <div
                      className={`flex items-center transition-all duration-300 ease-in-out overflow-hidden rounded-lg cursor-pointer h-9 sm:h-12 ${showSearch
                        ? "w-64 sm:w-96 border-2 border-[#FBD037] shadow-[0_6px_20px_rgba(251,208,55,0.15)]"
                        : "w-9 sm:w-12 hover:border-[#FBD037]"
                        }`}
                    >
                      <div
                        className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 text-[#212121] bg-transparent flex-shrink-0"
                        onClick={() => !showSearch && setShowSearch(true)}
                      >
                        <IconSearch
                          strokeWidth={1.5}
                          size={16}
                          className="sm:hidden"
                        />
                        <IconSearch
                          strokeWidth={1.5}
                          size={18}
                          className="hidden sm:block"
                        />
                      </div>

                      {showSearch && (
                        <>
                          <input
                            type="text"
                            placeholder={t("search awb, order...")}
                            autoFocus
                            value={searchFor}
                            onChange={(e) => setSearchFor(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            className="flex-1 h-full px-2 text-xs sm:text-sm text-[#212121] placeholder-[#212121]/70 bg-transparent border-none outline-none focus:outline-none focus:ring-0"
                          />
                          <div
                            className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 text-[#212121] flex-shrink-0"
                            onClick={() => {
                              setShowSearch(false);
                              setSearchFor("");
                              setSearchResults([]);
                            }}
                          >
                            <IconX className="w-4 h-4 sm:w-5 sm:h-5" />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-1.5 sm:space-x-3 rtl:space-x-reverse">
                  {/* Settings Icon */}
                  <div
                    className={`flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 border border-[#FFFFFF]/20 rounded-lg cursor-pointer transition-all hover:scale-105 duration-200 ${activeItem === "/settings"
                      ? "bg-[#212121] text-white font-bold shadow-sm"
                      : "bg-transparent text-[#212121] font-semibold hover:text-[#212121]"
                      }`}
                    onClick={() => navigate("/settings")}
                  >
                    <IconSettings
                      strokeWidth={1.5}
                      size={16}
                      className="sm:hidden"
                    />
                    <IconSettings
                      strokeWidth={1.5}
                      size={18}
                      className="hidden sm:block"
                    />
                  </div>

                  {/* Notifications */}
                  <div ref={notificationRef}>
                    <div
                      className={`relative flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 border border-[#FFFFFF]/20 rounded-lg cursor-pointer transition-all hover:scale-105 duration-200 ${showNotification
                        ? "bg-[#212121] text-white font-bold shadow-sm"
                        : "bg-transparent text-[#212121] font-semibold hover:text-[#212121]"
                        }`}
                      onClick={() => {
                        fetchNotifications();
                        setShowNotification((p) => !p);
                      }}
                    >
                      <IconBell
                        strokeWidth={1.5}
                        size={16}
                        className="sm:hidden"
                      />
                      <IconBell
                        strokeWidth={1.5}
                        size={18}
                        className="hidden sm:block"
                      />
                      {totalUnreadCount > 0 ? (
                        <span className="absolute -top-1 -end-1 flex items-center justify-center min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-[20px] px-1 text-[9px] sm:text-[10px] font-bold text-white bg-red-500 rounded-lg shadow-md">
                          {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
                        </span>
                      ) : null}
                    </div>

                    {showNotification && (
                      <div
                        className={`absolute ${language === "ar" ? "left-0" : "right-0"} z-[1000] w-72 sm:w-96 p-4 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg`}
                      >
                        <h3 className="mb-2 text-base sm:text-lg">
                          {t("Notifications")}
                        </h3>
                        <div className="max-h-96 overflow-y-auto">
                          {notificationsData?.notifications?.length ? (
                            notificationsData.notifications
                              .map((n: any) => (
                                <NotificationItem
                                  key={n.id}
                                  n={n}
                                  onClick={() => handleNotificationClick(n)}
                                />
                              ))
                          ) : (
                            <p className="py-6 text-sm text-center text-gray-500">
                              {t("No notifications")}
                            </p>
                          )}
                        </div>

                      </div>
                    )}
                  </div>

                  {/* Language Switcher */}
                  <div className="relative">
                    <div
                      className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 border border-[#FFFFFF]/20 rounded-lg cursor-pointer bg-white/20 transition-all hover:scale-105 duration-200"
                      onClick={() =>
                        setLanguage(language === "en" ? "ar" : "en")
                      }
                    >
                      <IconLanguage
                        strokeWidth={1.5}
                        size={16}
                        className="text-[#212121] sm:hidden"
                      />
                      <IconLanguage
                        strokeWidth={1.5}
                        size={18}
                        className="text-[#212121] hidden sm:block"
                      />
                    </div>
                  </div>

                  {/* Profile */}
                  <div className="relative" ref={profileRef}>
                    <div
                      className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 overflow-hidden rounded-lg cursor-pointer"
                      onClick={() => setShowProfileDropdown((p) => !p)}
                    >
                      <img
                        className="object-cover w-6 h-6 sm:w-8 sm:h-8 rounded-lg"
                        src={displayAvatar}
                        alt="Avatar"
                      />
                    </div>

                    {showProfileDropdown && (
                      <div
                        className={`absolute ${language === "ar" ? "left-0" : "right-0"} z-[1000] w-72 p-0 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg`}
                      >
                        <div className="flex items-center gap-2 px-4 py-4 border-b border-[#E5E7EB]">
                          <div className="relative group">
                            <img
                              className="w-14 h-14 object-cover rounded-lg"
                              src={displayAvatar}
                              alt="Avatar"
                            />
                            <div
                              onClick={() => fileInputRef.current?.click()}
                              className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg opacity-0 cursor-pointer group-hover:opacity-100 transition"
                            >
                              <IconPencil size={18} color="white" />
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              ref={fileInputRef}
                              className="hidden"
                              onChange={handleFileChange}
                            />
                          </div>
                          <div>
                            <h2 className="text-sm font-medium text-[#212121]">
                              {UserData?.name ??
                                UserData?.storeName ??
                                UserData?.username}
                            </h2>
                            <p className="text-xs text-[#212121]/70">
                              {UserData?.email}
                            </p>
                          </div>
                        </div>

                        <ul className="py-2 space-y-2 text-sm text-[#212121]">
                          {[
                            {
                              icon: IconUser,
                              label: "Company Info",
                              path: "/profile",
                            },
                            {
                              icon: IconSettings,
                              label: "Settings",
                              path: "/settings",
                            },
                            {
                              icon: IconHeadset,
                              label: "Help & Support",
                              path: "/help-and-support",
                            },
                          ].map((i) => (
                            <li
                              key={i.path}
                              className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                              onClick={() => {
                                navigate(i.path);
                                setShowProfileDropdown(false);
                              }}
                            >
                              <div className="flex items-center gap-1">
                                <i.icon stroke={1.5} />
                                <T>{i.label}</T>
                              </div>
                              <IconChevronRight
                                size={22}
                                stroke={1.5}
                                color="#6B7280"
                              />
                            </li>
                          ))}

                          <li
                            className="flex items-center gap-1 px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                            onClick={() => {
                              handleLogout();
                              setShowProfileDropdown(false);
                            }}
                          >
                            <IconLogout size={22} stroke={1.5} /> <T>Logout</T>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Hamburger Menu Icon */}
                  <div
                    onClick={() => setShowSidebar((p) => !p)}
                    className="flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 border border-[#FFFFFF]/20 rounded-lg cursor-pointer xl:hidden"
                  >
                    <IconMenu2 className="w-5 h-5 sm:w-6 sm:h-6 text-[#212121]/70" />
                  </div>
                </div>
              </div>
            </header>
          </div>

          {showSidebar && (
            <>
              <div
                className="fixed inset-0 z-40 bg-black/30"
                onClick={() => setShowSidebar(false)}
              />
              <aside className="fixed top-0 end-0 z-50 w-64 h-full p-4 overflow-y-auto bg-[#1f1f1f] border-s border-[#FFFFFF]/10 shadow-2xl xl:hidden">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-white">
                    <T>Menu</T>
                  </h2>
                  <IconX
                    onClick={() => setShowSidebar(false)}
                    className="w-6 h-6 text-white cursor-pointer"
                  />
                </div>
                <nav className="flex flex-col space-y-2">
                  {visibleNavItems.map((i) => (
                    <div
                      key={i.path}
                      onClick={() => handleNavClick(i.path)}
                      className={`px-4 py-3 text-sm rounded cursor-pointer flex items-center gap-2 ${activeItem === i.path
                        ? "bg-[#FBD037] text-black"
                        : "text-white hover:bg-white/10"
                        }`}
                    >
                      {i.icon}
                      <T>{i.name}</T>
                    </div>
                  ))}
                </nav>
              </aside>
            </>
          )}

          {openResponse && (
            <BulkResponseHandler
              isOpen={openResponse}
              onClose={() => setOpenResponse(false)}
              bulkJobId={bulkJobId}
              notification={selectedNotification}
            />
          )}
        </div>
      )}
    </>
  );
}
